import React, { useState } from "react";
import {
  EmailAuthProvider,
  isSignInWithEmailLink,
  linkWithCredential,
  signInWithEmailLink,
  signOut,
  updatePassword,
  updateProfile,
} from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { C } from "../theme";
import Button from "../components/Button";
import { auth, db } from "../firebase";
import { resolveApiBaseUrl, resolveHostedAppBaseUrl } from "../services/apiBase";

const EULA_VERSION = "2026-04-16";
const PENDING_KEY = "the99.pendingSignup";
const ACTIVATION_LINK_KEY = "the99.activationLink";

// Re-request a fresh sign-in (activation) link through our Cloud Function so
// the email is delivered by the SendGrid extension (matches AuthScreen).
async function requestActivationEmail(email) {
  const base = resolveApiBaseUrl() || "";
  const continueUrl = resolveHostedAppBaseUrl() || window.location.origin;
  const res = await fetch(`${base}/v1/auth/email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "signin_link",
      email,
      continueUrl,
      publicBaseUrl: continueUrl,
    }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const payload = await res.json();
      detail = payload?.error?.message || "";
    } catch (_) {}
    throw new Error(detail || `Email request failed (${res.status}).`);
  }
  return res.json();
}

function getPasswordErrors(password, email) {
  const errs = [];
  if (!password) return errs;
  if (password.length < 6) errs.push("At least 6 characters");
  if (password.length >= 72) errs.push("Fewer than 72 characters");
  if (!/\d/.test(password)) errs.push("A number");
  if (!/[!@#$%^&*()\-_=+[\]{};':",.<>/?\\|`~]/.test(password)) errs.push("A special character");
  if (!/[A-Z]/.test(password)) errs.push("An uppercase letter");
  if (!/[a-z]/.test(password)) errs.push("A lowercase letter");
  if (email && password.toLowerCase() === email.trim().toLowerCase()) errs.push("Different from your email address");
  return errs;
}

export default function ActivationScreen({ onActivated, onCancel }) {
  const [pending] = useState(() => {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [email, setEmail] = useState(pending.email || "");
  const [password, setPassword] = useState(pending.password || "");
  const [showPassword, setShowPassword] = useState(false);
  const [stage, setStage] = useState("password"); // "password" | "working"
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState("");

  // Capture the activation link the moment this screen mounts. App.jsx stores
  // the inbound URL in localStorage before anything can mutate the URL bar, so
  // prefer that; fall back to the live URL. Using a stable captured value means
  // a later URL cleanup can't make the link look "expired" at confirm time.
  const [activationLink] = useState(() => {
    try {
      return localStorage.getItem(ACTIVATION_LINK_KEY) || window.location.href;
    } catch {
      return window.location.href;
    }
  });

  const passwordErrors = getPasswordErrors(password, email);
  const passwordValid = password.length > 0 && passwordErrors.length === 0;

  const requirementRows = [
    { label: "At least 6 characters", pass: password.length >= 6 },
    { label: "Fewer than 72 characters", pass: password.length > 0 && password.length < 72 },
    { label: "Includes a number", pass: /\d/.test(password) },
    { label: "Includes a special character", pass: /[!@#$%^&*()\-_=+[\]{};':",.<>/?\\|`~]/.test(password) },
    { label: "Includes uppercase and lowercase letters", pass: /[A-Z]/.test(password) && /[a-z]/.test(password) },
    {
      label: "Different from your email address",
      pass: email.trim().length > 0 && password.length > 0 && password.toLowerCase() !== email.trim().toLowerCase(),
    },
  ];

  const handleConfirm = async (e) => {
    e.preventDefault();
    setTouched(true);
    setResendNotice("");
    if (!email.trim()) {
      setError("Enter the email address where you received the activation link.");
      return;
    }
    if (!passwordValid) {
      setError("Please fix all password requirements before continuing.");
      return;
    }
    // Use the captured link (not the live URL, which may have been cleaned).
    // Only block up front if we genuinely have no email-link to work with;
    // otherwise let Firebase be the source of truth on expiry/validity.
    const linkToUse =
      isSignInWithEmailLink(auth, activationLink)
        ? activationLink
        : isSignInWithEmailLink(auth, window.location.href)
        ? window.location.href
        : null;
    if (!linkToUse) {
      setLinkExpired(true);
      setError("We couldn't read your activation link. Send yourself a fresh one below.");
      return;
    }

    setError("");
    setStage("working");
    try {
      const result = await signInWithEmailLink(auth, email.trim(), linkToUse);

      // Activation link is single-use — drop the cached copy and clean the URL.
      try { localStorage.removeItem(ACTIVATION_LINK_KEY); } catch {}
      window.history.replaceState({}, document.title, window.location.pathname);

      const passwordCredential = EmailAuthProvider.credential(email.trim(), password);
      try {
        await linkWithCredential(result.user, passwordCredential);
      } catch (credentialErr) {
        if (
          credentialErr?.code === "auth/provider-already-linked" ||
          credentialErr?.code === "auth/email-already-in-use" ||
          credentialErr?.code === "auth/credential-already-in-use"
        ) {
          await updatePassword(result.user, password);
        } else {
          throw credentialErr;
        }
      }

      const displayName = [pending.firstName, pending.lastName].filter(Boolean).join(" ").trim();
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }

      const userPayload = {
        uid: result.user.uid,
        email: (email.trim() || result.user.email || "").trim(),
        loginProvider: "password",
        loginTime: serverTimestamp(),
      };

      if (pending.firstName) userPayload.firstName = String(pending.firstName).trim();
      if (pending.lastName) userPayload.lastName = String(pending.lastName).trim();
      if (pending.phone) userPayload.phone = String(pending.phone);
      if (pending.eulaAccepted) {
        userPayload.eula = {
          accepted: true,
          version: EULA_VERSION,
          acceptedAt: serverTimestamp(),
          acceptedVia: "email_signup",
        };
      }

      await setDoc(doc(db, "users", result.user.uid), userPayload, { merge: true });

      try { localStorage.removeItem(PENDING_KEY); } catch {}
      try { localStorage.removeItem("the99.emailForSignIn"); } catch {}
      try { localStorage.setItem("the99.currentLoginProvider", "password"); } catch {}

      onActivated?.();
    } catch (err) {
      const code = err?.code || "";
      if (
        code === "auth/expired-action-code" ||
        code === "auth/invalid-action-code" ||
        code === "auth/invalid-email"
      ) {
        try { localStorage.removeItem(ACTIVATION_LINK_KEY); } catch {}
        setLinkExpired(true);
        setError("This activation link has expired or was already used. Send yourself a fresh one below.");
      } else {
        setError(err?.message || "Activation failed. Please try again, or send yourself a fresh link below.");
      }
      setStage("password");
    }
  };

  const handleResend = async () => {
    setError("");
    setResendNotice("");
    const target = (email || pending.email || "").trim();
    if (!target) {
      setError("Enter the email address where you received the activation link.");
      return;
    }
    setResending(true);
    try {
      await requestActivationEmail(target.toLowerCase());
      try { localStorage.removeItem(ACTIVATION_LINK_KEY); } catch {}
      setLinkExpired(false);
      setResendNotice(`A fresh activation link is on its way to ${target}. Open it on this device to finish setup.`);
    } catch (err) {
      setError(err?.message || "We couldn't send a new link right now. Please try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  const handleCancel = async () => {
    try { await signOut(auth); } catch {}
    try { localStorage.removeItem(PENDING_KEY); } catch {}
    window.history.replaceState({}, document.title, window.location.pathname);
    onCancel?.();
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 12px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.cardBg,
    color: C.white,
    outline: "none",
  };

  return (
    <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#04060a" }}>
      <div
        className="hide-scrollbar"
        style={{
          position: "absolute",
          inset: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          padding: "24px 18px 40px",
          gap: 10,
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>THE 99</div>
          <div style={{ fontSize: 22, color: "#F5FAFF", fontFamily: "Georgia,serif", fontWeight: 700 }}>
            Activate Account
          </div>
          <div style={{ fontSize: 11, color: "rgba(224,236,247,0.86)", marginTop: 2 }}>
            Create your password to complete sign-up.
          </div>
        </div>

        <div
          style={{
            background: "rgba(4,8,15,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: 16,
            border: `1px solid ${C.border}`,
            padding: "16px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {/* Email */}
          <div>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 5 }}>Email address</div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={!!pending.email || stage === "working"}
              style={{
                ...inputStyle,
                opacity: pending.email ? 0.7 : 1,
                background: pending.email ? "rgba(6,10,16,0.45)" : C.cardBg,
              }}
            />
            {!pending.email && (
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                Enter the email address where you received the activation link.
              </div>
            )}
          </div>

          {/* Password form */}
          <form onSubmit={handleConfirm} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, color: C.muted, marginBottom: 5 }}>Create password</div>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setTouched(true); }}
                  placeholder="Password"
                  autoComplete="new-password"
                  disabled={stage === "working"}
                  style={{
                    ...inputStyle,
                    paddingRight: 42,
                    border: `1px solid ${touched && !passwordValid ? C.warning : C.border}`,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: C.muted,
                    cursor: "pointer",
                    padding: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Requirements checklist */}
            <div
              style={{
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                background: "rgba(6,10,16,0.55)",
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ fontSize: 10, color: C.white, fontWeight: 700, marginBottom: 2 }}>
                Password must contain:
              </div>
              {requirementRows.map(({ label, pass }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    fontSize: 10,
                    color: pass ? C.seafoam : C.muted,
                    transition: "color 0.15s",
                  }}
                >
                  <span style={{ width: 14, textAlign: "center", fontWeight: 700 }}>
                    {pass ? "✓" : "○"}
                  </span>
                  <span>{label}</span>
                </div>
              ))}
            </div>

            {error && (
              <div style={{ fontSize: 11, color: C.warning, lineHeight: 1.45 }}>{error}</div>
            )}

            {resendNotice && (
              <div style={{ fontSize: 11, color: C.seafoam, lineHeight: 1.45 }}>{resendNotice}</div>
            )}

            {(linkExpired || error) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleResend}
                disabled={resending || stage === "working"}
                style={{ alignSelf: "flex-start" }}
              >
                {resending ? "Sending new link…" : "Email me a new activation link"}
              </Button>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <Button
                type="submit"
                variant="primary"
                disabled={stage === "working" || !passwordValid || !email.trim()}
                style={{ flex: 1 }}
              >
                {stage === "working" ? "Activating..." : "Confirm"}
              </Button>
              <Button
                variant="ghost"
                onClick={handleCancel}
                disabled={stage === "working"}
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
